import React from 'react';
import './SetPieces.css';
import Timeline from '../Timeline/Timeline';
import StageCanvas, { StageCanvasMode } from '../StageCanvas/StageCanvas';
import useActions from '../../hooks/use-actions';
import useTypedSelector from '../../hooks/use-type-selectors';
import { Units } from '../../models/Settings';
import { ISetPiece, SetPieceColor, SetPieceShape } from '../../models/SetPieces';
import { centimetersToFeetRounded, centimetersToMetersRounded, feetToCentimeters, metersToCentimeters } from '../../utils/general';
import MeasureInput from '../MeasureInput';

const SetPieces: React.FC = () => {
    const { addSetPiece, setSelectedSetPiece, updateSetPieceName, updateSetPieceAppearance, setSetPieceRect, deleteSetPiece, setAudiencePosRotation } = useActions();
    const settings = useTypedSelector(state => state.settings);
    const setPieces = useTypedSelector(state => state.undoable.setPieces);

    function giveScreenReaderFeedback(text: string) {
        const feedbackBox = document.querySelector("#feedback");
        feedbackBox!.innerHTML = "";
        const textnode = document.createTextNode(text);
        feedbackBox?.appendChild(textnode);
    }

    function addSetPieceFeedback () {
        addSetPiece();
        giveScreenReaderFeedback('set piece added');
    }

    function deleteSetPieceFeedback () {
        deleteSetPiece(Number(setPieces.selectedSetPieceId));
        setSelectedSetPiece("audience");
        giveScreenReaderFeedback('deleted. audience selected');
    }

    function updateSelectedSetPiece () {
        var element = (document.getElementById("setPieceSelect")) as HTMLSelectElement;
        var opt = element.options[element.selectedIndex];

        if(opt.value === "audience") setSelectedSetPiece("audience");
        else {
            setSelectedSetPiece(Number(opt.value));
        }
    }

    function updateCurrentSetPieceName (e: React.ChangeEvent<HTMLInputElement>) {
        if (setPieces.selectedSetPieceId !== "audience") updateSetPieceName(Number(setPieces.selectedSetPieceId), e.target.value);
    }

    function updateCurrentSetPieceAppearence () {
        if (setPieces.selectedSetPieceId === "audience") return;

        var shapeSelected = ((document.getElementById("setPieceShape")) as HTMLSelectElement).selectedIndex;
        var colorSelected = ((document.getElementById("setPieceColor")) as HTMLSelectElement).selectedIndex;
        updateSetPieceAppearance(Number(setPieces.selectedSetPieceId), colorSelected, shapeSelected);
    }

    function updateCurrentSetPieceLength (length: number) {
        if (setPieces.selectedSetPieceId === "audience") return;

        if (isNaN(length)) length = 0;
        
        let feedback = '';
        if (settings.units === Units.Metric) {
            feedback = 'set piece length is ' + centimetersToMetersRounded(length) + ' meters';
        }
        else {
            feedback = 'set piece length is ' + centimetersToFeetRounded(length) + ' feet';
        }
        giveScreenReaderFeedback(feedback);
        
        var currSetPiece = getCurrentSetPiece();
        setSetPieceRect(
            Number(setPieces.selectedSetPieceId), 
            { x: currSetPiece.pos.x, y: currSetPiece.pos.y },
            { x: currSetPiece.size.x, y: length },
            currSetPiece.rotation
        );
    }

    function updateCurrentSetPieceWidth (width: number) {
        if (setPieces.selectedSetPieceId === "audience") return;

        if (isNaN(width)) width = 0;
        
        let feedback = '';
        if (settings.units === Units.Metric) {
            feedback = 'set piece width is ' + centimetersToMetersRounded(width) + ' meters';
        }
        else {
            feedback = 'set piece width is ' + centimetersToFeetRounded(width) + ' feet';
        }
        giveScreenReaderFeedback(feedback);
        
        var currSetPiece = getCurrentSetPiece();
        setSetPieceRect(
            Number(setPieces.selectedSetPieceId), 
            { x: currSetPiece.pos.x, y: currSetPiece.pos.y },
            { x: width, y: currSetPiece.size.y },
            currSetPiece.rotation
        );
    }

    function updateCurrentSetPieceOrientation () {
        var orientation = parseFloat((document.getElementById("setPieceOrientation") as HTMLInputElement).value);
        if (isNaN(orientation)) orientation = 0;

        if (setPieces.selectedSetPieceId === "audience") {
            let feedback = 'audience orientation is ' + orientation + ' degrees';
            giveScreenReaderFeedback(feedback);
            setAudiencePosRotation({ x: setPieces.audience.pos.x, y: setPieces.audience.pos.y }, orientation);
        }
        else {
            let feedback = 'set piece orientation is ' + orientation + ' degrees';
            giveScreenReaderFeedback(feedback);
            var currSetPiece = getCurrentSetPiece();
            setSetPieceRect(
                Number(setPieces.selectedSetPieceId), 
                { x: currSetPiece.pos.x, y: currSetPiece.pos.y },
                { x: currSetPiece.size.x, y: currSetPiece.size.y },
                orientation
            );
        }
    }

    function updateCurrentSetPieceLocation () {
        var locationWidth = parseFloat((document.getElementById("setPieceLocationWidth") as HTMLInputElement).value);
        var locationLength = parseFloat((document.getElementById("setPieceLocationLength") as HTMLInputElement).value);
        if (isNaN(locationWidth)) locationWidth = 0;
        if (isNaN(locationLength)) locationLength = 0;

        if (settings.units === Units.Metric) {
            locationWidth = metersToCentimeters(locationWidth);
            locationLength = metersToCentimeters(locationLength);
        }
        else {
            locationWidth = feetToCentimeters(locationWidth);
            locationLength = feetToCentimeters(locationLength);
        }

        if (setPieces.selectedSetPieceId === "audience") {
            let feedback = '';
            if (settings.units === Units.Metric) {
                feedback = 'audience X location is ' + centimetersToMetersRounded(locationWidth) + ' meters and Y location is ' + centimetersToMetersRounded(locationLength) + ' meters';
            }
            else {
                feedback = 'audience X location is ' + centimetersToMetersRounded(locationWidth) + ' feet and Y location is ' + centimetersToMetersRounded(locationLength) + ' feet';
            }
            giveScreenReaderFeedback(feedback);

            setAudiencePosRotation({ x: locationWidth, y: locationLength }, setPieces.audience.rotation);
        } else {
            let feedback = '';
            if (settings.units === Units.Metric) {
                feedback = 'set piece X location is ' + centimetersToMetersRounded(locationWidth) + ' meters and Y location is ' + centimetersToMetersRounded(locationLength) + ' meters';
            }
            else {
                feedback = 'set piece X location is ' + centimetersToMetersRounded(locationWidth) + ' feet and Y location is ' + centimetersToMetersRounded(locationLength) + ' feet';
            }
            giveScreenReaderFeedback(feedback);

            var currSetPiece = getCurrentSetPiece();
            setSetPieceRect(
                Number(setPieces.selectedSetPieceId), 
                { x: locationWidth, y: locationLength },
                { x: currSetPiece.size.x, y: currSetPiece.size.y },
                currSetPiece.rotation
            );
        }
    }

    function getCurrentSetPiece () : ISetPiece {
        return setPieces.setPieces[setPieces.setPieces.findIndex(sp => sp.id === setPieces.selectedSetPieceId)];
    }

    return (
        <div className='gridContainer'>
            <div className='sideBar'>
                <div style={{ display: 'flex', alignItems:'center', marginBottom: '20px' }}>
                    <img className='setPiecesIcon' alt='Set pieces' aria-hidden/>
                    <h1 style={{ whiteSpace: 'nowrap', margin: '0' }}>SET PIECES</h1>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', width:'100%' }}>
                        <button aria-label={'Add set piece ' + setPieces.nextSetPieceId} aria-controls='feedback' className='addHeaderButton' onClick={addSetPieceFeedback}/>
                    </div>
                </div>
                <div style={{ display: 'flex' }}>
                    <select aria-label='set piece list' id='setPieceSelect' className='dropdown' value={setPieces.selectedSetPieceId === "audience" ? "audience" : Number(setPieces.selectedSetPieceId)} onChange={updateSelectedSetPiece}>
                        <option value="audience">Audience</option>
                        { setPieces.setPieces.map(setPiece => { 
                            return <option value={setPiece.id}>{setPiece.name}</option>
                        })}
                    </select>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }} >
                    {setPieces.selectedSetPieceId === "audience" ? 
                        <button aria-label='unable to delete audience' aria-controls='feedback' className='removeFromDropdown-Disabled' disabled/>
                        :
                        <button aria-label={`delete ${getCurrentSetPiece().name}`} aria-controls='feedback' className='removeFromDropdown' onClick={deleteSetPieceFeedback}/>
                    }
                    </div>
                </div>
                <hr className='divider' aria-hidden />
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <div className='subtitle' style={{ width: '30%' }}>Name</div>
                    <div className='elementValue' style={{ width:'70%' }}>
                        <input aria-label='edit set piece name' type='text' className='selectedOptionName' onChange={updateCurrentSetPieceName}
                            value={setPieces.selectedSetPieceId === "audience" ? "Audience" : getCurrentSetPiece().name} readOnly={setPieces.selectedSetPieceId === "audience"}/>
                    </div>
                </div>
                {setPieces.selectedSetPieceId !== "audience" &&
                    <div className='responsiveContainer'>
                        <select id='setPieceColor' aria-label='set piece colour is' value={getCurrentSetPiece().color} onChange={updateCurrentSetPieceAppearence} 
                            className={`dropdown-${SetPieceColor[getCurrentSetPiece().color]}`} style={{ marginTop: '20px' }}>
                            {Object.keys(SetPieceColor).filter(color => isNaN(Number(color))).map((color, index) => 
                                <option value={index}>{color}</option>
                            )}  
                        </select>
                        <select id='setPieceShape' aria-label='set piece shape is' value={getCurrentSetPiece().shape} onChange={updateCurrentSetPieceAppearence} 
                            className={`dropdown-${SetPieceShape[getCurrentSetPiece().shape]}`} style={{ marginTop: '20px' }}>
                            {Object.keys(SetPieceShape).filter(shape => isNaN(Number(shape))).map((shape, index) => 
                                <option value={index}>{shape}</option>
                            )}  
                        </select>
                    </div>
                }
                <hr className='divider' aria-hidden />
                {setPieces.selectedSetPieceId !== "audience" &&
                <>
                    <MeasureInput label='Length' value={getCurrentSetPiece().size.y} max={10000} onChangeFunc={updateCurrentSetPieceLength} ariaLabel='set piece length' />
                    <MeasureInput label='Width' value={getCurrentSetPiece().size.x} max={10000} onChangeFunc={updateCurrentSetPieceWidth} ariaLabel='set piece width' />
                </>
                }
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: '20px'}}>
                    <div className='subtitle' style={{ width: '55%' }}>Orientation</div>
                    <div className='elementValue' style={{ width: '45%' }}>
                        <input id='setPieceOrientation' aria-label='set piece orientation degrees' type='number' className='inputBox' onChange={updateCurrentSetPieceOrientation}
                            value={setPieces.selectedSetPieceId === "audience" ? setPieces.audience.rotation.toFixed(0) : getCurrentSetPiece().rotation.toFixed(0) }/>
                        <label className='subtitle'>&deg;</label>
                    </div> 
                </div>
                <hr className='divider' aria-hidden />
                <div className='locationContainer'>
                    <div className='locationLabel'>Location (X,Y)</div>
                    <div className='locationValue'>
                        <input id='setPieceLocationWidth' type='number' step='0.01' className='inputBox' style={{ marginLeft: '10px' }} onChange={updateCurrentSetPieceLocation}
                            aria-label={settings.units === Units.Metric ? 'set piece X location meters' : 'set piece X location feet'} aria-live="assertive"
                            key={`width${setPieces.selectedSetPieceId}`}
                            value={setPieces.selectedSetPieceId === "audience"
                                ? (settings.units === Units.Metric ? centimetersToMetersRounded(setPieces.audience.pos.x) : centimetersToFeetRounded(setPieces.audience.pos.x)) 
                                : (settings.units === Units.Metric ? centimetersToMetersRounded(getCurrentSetPiece().pos.x) : centimetersToFeetRounded(getCurrentSetPiece().pos.x))}/>
                        ,
                        <input id='setPieceLocationLength' type='number' step='0.01' className='inputBox' style={{ marginLeft: '10px' }} onChange={updateCurrentSetPieceLocation}
                            aria-label={settings.units === Units.Metric ? 'set piece Y location meters' : 'set piece Y location feet'} aria-live="assertive"
                            key={`height${setPieces.selectedSetPieceId}`}
                            value={setPieces.selectedSetPieceId === "audience"
                                ? (settings.units === Units.Metric ? centimetersToMetersRounded(setPieces.audience.pos.y) : centimetersToFeetRounded(setPieces.audience.pos.y)) 
                                : (settings.units === Units.Metric ? centimetersToMetersRounded(getCurrentSetPiece().pos.y) : centimetersToFeetRounded(getCurrentSetPiece().pos.y))}/>
                        { settings.units === Units.Metric ? <>m</> : <>ft</> }
                    </div>
                </div>
            </div>
            <div className='stage'>
                <StageCanvas mode={StageCanvasMode.SetPiecesAndAudience} />
            </div>
            <div className='timeline'>
                <Timeline />
            </div>
        </div>
    )
};

export default SetPieces;